import { AxiosError, AxiosRequestConfig, Method } from 'axios';
import { URLSearchParams } from 'url';

import { UserSessionEvent } from '../types/auth';
import { getJWT } from '../utils/auth';
import API from './api';

const HTTP_STATUS_CODE = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

export type RequestOptionsType = {
  method?: Method;
  data?: any;
  authRequired?: boolean;
  retry?: boolean;
  params?: Record<string, any> | URLSearchParams;
  responseType?: AxiosRequestConfig['responseType'];
};

const getHeaders = (authRequired: boolean) => {
  const headers: any = {};
  if (authRequired) {
    const token = getJWT();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return headers;
};

type RequireOne<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

export type AxiosErrorWithResponse<T = any> = RequireOne<
  AxiosError<T>,
  'response'
>;

export type AxiosResultErrorCustomHttp<C extends string, T = any> = {
  code: C;
  error: AxiosErrorWithResponse;
  status: number;
  data?: T;
};

type AxiosResultErrorHttp<T = any> = {
  code: 'HTTP_ERROR';
  error: AxiosErrorWithResponse;
  status: number;
  data?: T;
};

type AxiosResultErrorConnection = {
  code: 'CONNECTION';
  error: AxiosError;
  status?: number;
  data?: null;
};

export type AuthenticationError = {
  code: 'AUTHENTICATION';
  error: AxiosErrorWithResponse;
  status: number;
  data?: any;
};

export type AxiosResultDefaultError =
  | AxiosResultErrorHttp
  | AxiosResultErrorConnection;

export type AxiosResult<
  T,
  E extends { code: string; data?: any } = AxiosResultDefaultError,
> =
  | { data: T; error?: false; status?: number }
  | { data?: null; error: E; status?: null };

const request = async <T = any>(
  url: string,
  options: RequestOptionsType = {},
): Promise<AxiosResult<T>> => {
  const {
    method = 'get',
    data = {},
    authRequired = true,
    params,
    responseType,
  } = options;
  try {
    const response = await API.request<T>({
      url: url,
      params,
      method: method,
      data: data,
      headers: getHeaders(authRequired),
      responseType,
    });
    return { data: response.data, error: false, status: response.status };
  } catch (error: any) {
    if (error.response) {
      // Token invalido
      if (error.response.status === 401 && authRequired) {
        const sessionExpiredEvent = new CustomEvent(UserSessionEvent.EXPIRED);
        window.dispatchEvent(sessionExpiredEvent);
      }

      return {
        error: {
          code: 'HTTP_ERROR',
          status: error.response.status,
          data: error.response.data,
          error,
        },
      };
    }
    return {
      error: {
        code: 'CONNECTION',
        error,
      },
    };
  }
};

export { HTTP_STATUS_CODE };
export default request;
